/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { Modal, Tooltip } from 'antd'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Text from 'src/components/ts-components/Text'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import HockeyUSALogo from './HockeyUSALogo'
import WarningIcon from 'src/components/icons/WarningIcon'
import React from 'react'

/**
 * Props interface for the HockeyUSAIntegrationCard component
 */
interface HockeyUSAIntegrationCardProps {
  /** Whether the user has an Ultimate account */
  isUltimateAccount: boolean
  /** Whether USA Hockey integration is connected */
  isHockeyUSAConnected: boolean
  /** Whether USA Hockey integration is enabled */
  isHockeyUSAEnabled: boolean
  /** Whether to show the USA Hockey warning modal */
  showHockeyUSAWarning: boolean
  /** Function to handle USA Hockey toggle switch */
  handleHockeyUSAToggle: () => void
  /** Function to confirm USA Hockey integration enable */
  confirmHockeyUSAEnable: () => void
  /** Function to cancel USA Hockey integration enable */
  cancelHockeyUSAEnable: () => void
  /** Function to call when switching to Ultimate account page */
  setCurrentPage: (page: number) => void
  /** The name of the user's current account type */
  accountTypeName: string
}

/**
 * HockeyUSAIntegrationCard - A self-contained component for managing USA Hockey integration
 *
 * This component handles all UI related to USA Hockey integration including:
 * - Integration card display with connection status
 * - Warning modal for cost confirmation when enabling the integration
 * - Switch toggle for enabling/disabling the integration
 *
 * @param props - Component props containing integration state and handlers
 * @returns JSX element containing the USA Hockey integration card and warning modal
 */
const HockeyUSAIntegrationCard: React.FC<HockeyUSAIntegrationCardProps> = ({
  isUltimateAccount,
  isHockeyUSAConnected,
  isHockeyUSAEnabled,
  showHockeyUSAWarning,
  handleHockeyUSAToggle,
  confirmHockeyUSAEnable,
  cancelHockeyUSAEnable,
  setCurrentPage,
  accountTypeName,
}) => {
  console.log(
    '🔄 HockeyUSAIntegrationCard: Rendering USA Hockey integration card'
  )

  const tooltipTitle = (
    <span
      css={css`
        font-size: 12px;
      `}
    >
      You are currently using a{' '}
      <b
        css={css`
          color: ${colors.PRIMARY};
        `}
      >
        {accountTypeName}
      </b>{' '}
      account.
      <br />
      Upgrade your account to get all the features on the SportNinja platform.{' '}
      <a
        onClick={(e) => {
          e.stopPropagation()
          setCurrentPage(1)
        }}
        css={css`
          color: ${colors.PRIMARY};
          text-decoration: underline;
          cursor: pointer;
        `}
      >
        Click here to upgrade
      </a>
    </span>
  )

  return (
    <>
      {/* USA Hockey Integration Card */}
      <Tooltip title={!isUltimateAccount ? tooltipTitle : ''} placement='top'>
        <div
          css={css`
            display: flex;
            padding: 16px;
            flex-direction: column;
            justify-content: space-between;
            flex: 1;
            min-height: 180px;
            border-radius: 4px;
            border: ${isHockeyUSAConnected
              ? '1px dashed var(--Neutrals-Gray60, #3f4753)'
              : '1px solid var(--Neutrals-Gray60, #3f4753)'};
            background-color: transparent;
            transition: background-color 0.2s ease;
            ${!isUltimateAccount &&
            `
              opacity: 0.6;
              cursor: pointer;
              &:hover {
                background-color: rgba(255, 255, 255, 0.05);
              }
            `}
          `}
          onClick={() => {
            if (!isUltimateAccount) {
              setCurrentPage(1)
            }
          }}
        >
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            `}
          >
            <HockeyUSALogo width={120} />
            <span
              css={css`
                color: ${colors.PRIMARY_200};
                text-align: right;
                font-size: 12px;
                font-weight: 400;
                line-height: 100%; /* 12px */
              `}
            >
              Included in Ultimate
            </span>
          </div>
          <span
            css={css`
              align-self: stretch;
              color: #fff;
              font-size: 12px;
              font-weight: 500;
              line-height: 120%; /* 14.4px */
              width: 100%;
              margin-top: 16px;
            `}
          >
            Validate USA Hockey member numbers for players during registration
            and ensure compliance with USA Hockey requirements.
          </span>
          {/* USA Hockey Switch */}
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              padding-top: 16px;
              border-top: 1px solid var(--Neutrals-Gray60, #3f4753);
              margin-top: 16px;
            `}
          >
            <SwitchInput
              title='Enable Integration'
              subtitle='Allow USA Hockey member validation'
              value={isHockeyUSAEnabled}
              onChange={handleHockeyUSAToggle}
              disabled={!isUltimateAccount}
            />
          </div>
        </div>
      </Tooltip>

      {/* USA Hockey Warning Modal */}
      <Modal
        css={css`
          .ant-modal-content {
            border-radius: 16px;
            background: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
          }
          .ant-modal-body {
            // padding: 16px;
          }
          .ant-modal-header {
            display: none;
          }
        `}
        width={480}
        open={showHockeyUSAWarning}
        onCancel={cancelHockeyUSAEnable}
        centered
        maskClosable={false}
        footer={null}
        styles={{
          content: {
            backgroundColor: colors.SECONDARY_300,
          },
          wrapper: {
            zIndex: 10002,
          },
          mask: {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(6, 10, 16, 0.8)',
            zIndex: 10001,
          },
        }}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            gap: 16px;
            position: relative;
          `}
        >
          <WarningIcon size={48} color={colors.PRIMARY} />

          <Text
            variant='heading1'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              font-family: ${
                isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
              };
              font-size: 36px;
              font-weight: 600;
              line-height: 36px;
              text-align: center;
            `}
          >
            Enable USA Hockey Integration
          </Text>

          <div
            css={css`
              align-self: stretch;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 8px;
              display: flex;
            `}
          >
            <div
              css={css`
                align-self: stretch;
                text-align: center;
              `}
            >
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.WHITE}
                style={`
                  font-family: Inter, Arial, Helvetica, sans-serif;
                  font-size: 16px;
                  font-weight: 400;
                  line-height: 23.2px;
                `}
              >
                This will enable USA Hockey member validation during player
                registration. Please note that each validation check incurs a
                cost. Are you sure you want to continue?
              </Text>
            </div>
          </div>

          <div
            css={css`
              align-self: stretch;
              height: 1px;
              background: ${colors.SECONDARY_100};
            `}
          />

          <div
            css={css`
              align-self: stretch;
              justify-content: center;
              align-items: flex-start;
              gap: 16px;
              display: flex;
              flex-direction: column;
            `}
          >
            <button
              css={css`
                width: 100%;
                padding: 8px 24px;
                background: ${colors.PRIMARY};
                border-radius: 8px;
                border: 2px solid ${colors.PRIMARY};
                justify-content: center;
                align-items: center;
                gap: 8px;
                display: flex;
                cursor: pointer;
                transition: all 0.2s;
                &:hover {
                  opacity: 0.8;
                }
              `}
              onClick={confirmHockeyUSAEnable}
            >
              <Text
                variant='heading6'
                weight='semiBold'
                color={PRIMARY_BUTTON_CONTENT.text}
                style={`
                  font-family: ${
                    isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                  };
                  font-size: 20px;
                  font-weight: 600;
                  line-height: 24px;
                `}
              >
                ENABLE INTEGRATION
              </Text>
            </button>
            <button
              css={css`
                width: 100%;
                padding: 8px 24px;
                border-radius: 8px;
                border: 2px solid ${colors.WHITE};
                background: transparent;
                justify-content: center;
                align-items: center;
                gap: 8px;
                display: flex;
                cursor: pointer;
                transition: all 0.2s;
                &:hover {
                  background: rgba(255, 255, 255, 0.1);
                }
              `}
              onClick={cancelHockeyUSAEnable}
            >
              <Text
                variant='heading6'
                weight='semiBold'
                color={colors.WHITE}
                style={`
                  font-family: ${
                    isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                  };
                  font-size: 20px;
                  font-weight: 600;
                  line-height: 24px;
                `}
              >
                CANCEL
              </Text>
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default HockeyUSAIntegrationCard
